import * as React from 'react';
import PropTypes from 'prop-types';
import { LoggerContext } from './context.js';

/**
 * Logger instance with Log4j interface
 *
 * @typedef {Object} Log4jLogger
 *
 * @property {Function} fatal
 * @property {Function} error
 * @property {Function} warn
 * @property {Function} info
 * @property {Function} debug
 * @property {Function} trace
 *
 * @access public
 */

/**
 * React Context provider for logger
 *
 * @example // Usage with built-in console logger
 * import * as Herz from '@rushplay/herz'
 *
 * const consoleLoggerInstance = {
 *   fatal: console.error,
 *   error: console.error,
 *   warn: console.warn,
 *   info: console.log,
 *   debug: (...args) => {
 *     if (process.env.NODE_ENV === 'debug') {
 *       console.log(...args)
 *     }
 *   },
 *   trace: (...args) => {
 *     if (process.env.NODE_ENV === 'debug') {
 *       console.log(...args)
 *     }
 *   },
 * }
 *
 * function App() {
 *   return (
 *     <Herz.Logging.LoggerProvider instance={consoleLoggerInstance}>
 *       <div>Your application code</div>
 *     </Herz.Logger.Provider>
 *   )
 * }
 *
 * @example // Usage with Pino
 * import * as Herz from '@rushplay/herz'
 * import createLogger from 'pino'
 *
 * const pinoLoggerInstance = createLogger({
 *   level: process.env.LOG_LEVEL,
 *   prettyPrint: process.env.NODE_ENV !== 'production',
 * })
 *
 * function App() {
 *   return (
 *     <Herz.Logging.LoggerProvider instance={pinoLoggerInstance}>
 *       <div>Your application code</div>
 *     </Herz.Logger.Provider>
 *   )
 * }
 *
 * @param {Object} props Component props
 * @param {Log4jLogger} props.logger Log4j-compatible logger instance
 *
 * @access public
 */

function LoggerProvider(props) {
  return /*#__PURE__*/React.createElement(LoggerContext.Provider, {
    value: props.instance
  }, props.children);
}
LoggerProvider.propTypes = {
  children: PropTypes.node,
  instance: PropTypes.shape({
    fatal: PropTypes.func.isRequired,
    error: PropTypes.func.isRequired,
    warn: PropTypes.func.isRequired,
    info: PropTypes.func.isRequired,
    debug: PropTypes.func.isRequired,
    trace: PropTypes.func.isRequired
  }).isRequired
};
/**
 * Retrieve Log4j-compatible logger instance
 *
 * Returns no-op logger if used without context provider. This allows to create
 * reusable components that are hooked into logging infrastructure, but does
 * not require one.
 *
 * @example // Basic usage
 * import * as Herz from '@rushplay/herz'
 *
 * export function Component(props) {
 *   const logger = Herz.Logger.useInstance()
 *   logger.trace({ id: props.id }, 'rendered component')
 *   return <div>{props.id}</div>
 * }
 *
 * @returns {Log4jLogger}
 *
 * @access public
 */

function useInstance() {
  return React.useContext(LoggerContext);
}

export { LoggerProvider, useInstance };
